import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../../api/axios";
import Modal from "../../components/common/AdminModal";
import Toast from "../../components/common/Toast";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import { useMainCompany } from '../../context/MainCompanyContext';
import { useRoleTheme } from "../../context/RoleThemeContext";
import { PlusCircle, Pencil, Loader2, Trash, RefreshCw, UserCheck, ShieldOff, Target, Home, AlertTriangle, Camera, ZoomIn, Lock, Building2, User } from "lucide-react";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../components/ui/Tooltip';
import { SelectedLocationsDisplay, PersonalFields, StatesSelectorMulti, StatesCheckboxList, CountiesPanel } from '../../components/admin/SalesRepFormParts';
import useDebounce from '../../hooks/useDebounce';
import getArray from '../../utils/getArray';
import useCountyCache from '../../hooks/useCountyCache';
import buildSelectedLocations from '../../utils/buildSelectedLocations';
import parseOptionValue from '../../utils/parseOptionValue';
import normalizeRep from '../../utils/normalizeRep';
import config from '../../config/config';

/** List rows use `name`; detail API often has only firstname/lastname. */
function salesRepDisplayName(rep) {
  if (!rep) return 'Sales Rep';
  const named = rep.name != null ? String(rep.name).trim() : '';
  if (named) return named;
  const parts = `${rep.firstname || ''} ${rep.lastname || ''}`.trim();
  if (parts) return parts;
  return 'Sales Rep';
}

/** Username from sales-rep detail payload (field names vary by API version). */
function repUsernameFromApi(rep) {
  if (!rep) return "";
  const nested = rep.user && typeof rep.user === "object" ? rep.user : null;
  const u =
    rep.username ??
    rep.userName ??
    rep.user_name ??
    rep.login ??
    rep.user_login ??
    nested?.username ??
    nested?.userName ??
    "";
  return String(u).trim();
}

export default function AdminSalesReps() {
  // primary lists
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [reps, setReps] = useState([]);
  const [saving, setSaving] = useState(false);
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { roleTheme } = useRoleTheme();
  const primaryBtnClasses = `${roleTheme?.colors?.buttons?.primary || 'bg-blue-600 hover:bg-blue-700'} text-white`;

  // filter selections for listing
  const [stateId, setStateId] = useState(0);
  const [countyId, setCountyId] = useState(0);
  // modal-local state so modal selections don't affect listing filters
  // support multi-select: modalStateIds is array of state ids
  // modalCountiesMap caches counties per state: { [stateId]: [...] }
  // modalSelectedCounties holds selected county ids per state: { [stateId]: [countyId, ...] }
  const [modalStateIds, setModalStateIds] = useState([]);
  const [modalCountiesMap, setModalCountiesMap] = useState({});
  const [modalSelectedCounties, setModalSelectedCounties] = useState({});
  // server-reported missing state/county info when saving edits
  const [modalMissing, setModalMissing] = useState({}); // { [stateId]: [countyId, ...] }
  const [modalMissingUsers, setModalMissingUsers] = useState([]);

  // selection for bulk actions
  const [selected, setSelected] = useState([]);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // filters
  const [filters, setFilters] = useState({ query: "", state: "", county: "", createdFrom: "", createdTo: "" });
  // Search input mirrors AdminUsers: keep searchQuery separate and debounce it for server-side requests
  const [searchQuery, setSearchQuery] = useState(filters.query || "");
  useDebounce(searchQuery, 500);
  useDebounce(filters, 300);

  // modal state
  const [modal, setModal] = useState({ type: null, data: null });

  // create / edit form state
  // we'll store firstname / lastname separately for better backend compatibility
  /** user_type: S = Sales Rep, SM = Sales Rep Manager (matches /auth/check-email and admin user filters) */
  const initialFormSplit = { user_type: "S", do_not_show_on_cf: false, firstname: "", lastname: "", email: "", phone: "", title: "", state: "", county: "", address1: "", address2: "", address_city: "", address_state: "", address_county: "", address_zip: "" };
  const [form, setForm] = useState(initialFormSplit);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});
  const [editImgError, setEditImgError] = useState(false);
  const [editProfilePreviewOpen, setEditProfilePreviewOpen] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editConfirmPassword, setEditConfirmPassword] = useState("");
  const [changeUsername, setChangeUsername] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [createUsername, setCreateUsername] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createConfirmPassword, setCreateConfirmPassword] = useState("");
  const emailValidRef = useRef(null);
  const usernameValidRef = useRef(null);

  useEffect(() => {
    if (modal.type !== 'edit') setEditProfilePreviewOpen(false);
  }, [modal.type]);

  // toast state (simple in-component toast like other admin pages)
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const showToast = useCallback((message, type = 'success', duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  }, []);

  const { selectedMain, mainCompanies } = useMainCompany();
  const location = useLocation();

  // load states
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const params = {
          ...(selectedMain ? { maincompanyid: selectedMain } : {})
        };
        const resp = await api.get('/admin/states', { params });
        const items = getArray(resp?.data ?? []);
        if (!cancelled) setStates(items);
      } catch (e) {
        if (!cancelled) setStates([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedMain]);

  // load counties when state changes (for the list filters)
  useEffect(() => {
    let cancelled = false;
    if (!stateId) {
      setCounties([]);
      setCountyId(0);
      return;
    }
    const load = async () => {
      try {
        const params = { stateId };
        if (selectedMain) params.maincompanyid = selectedMain;
        const resp = await api.get('/auth/counties', { params });
        const items = getArray(resp?.data ?? []);
        if (!cancelled) setCounties(items);
      } catch (e) {
        if (!cancelled) setCounties([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [stateId, selectedMain]);

  // county cache hook (keeps a local cache and exposes helpers)
  const { cache: countyCache, fetchCountiesForState, setCache } = useCountyCache(selectedMain);

  // keep modalCountiesMap in sync with the hook cache when it changes
  useEffect(() => {
    setModalCountiesMap((m) => ({ ...countyCache, ...m }));
  }, [countyCache]);

  // parseOptionValue and normalizeRep are imported from utils

  // fetch list (supports filtering by state/county and main company)
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: pageSize });

  const fetchReps = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        q: searchQuery || undefined,
        page,
        limit: pageSize,
        maincompanyid: selectedMain || undefined,
      };
      if (stateId) params.stateid = stateId;
      if (countyId) params.countyid = countyId;
      // Date filters not implemented in API yet - commented out to avoid confusion
      // if (filters.createdFrom) params.createdFrom = filters.createdFrom;
      // if (filters.createdTo) params.createdTo = filters.createdTo;
      const { data } = await api.get(`/admin/sales-reps/${selectedMain}`, { params });
      // map to normalized shape
      const body = data;
      const items = getArray(body?.data ?? body);
      const mapped = items.map(normalizeRep);
      setReps(mapped);
      setMeta(body?.meta || { total: mapped.length, page, limit: pageSize });
      setError("");
    } catch (err) {
      console.error("Failed to load reps", err);
      setError("Could not load sales reps. Retry.");
    } finally {
      setLoading(false);
    }
  }, [selectedMain, page, pageSize, searchQuery, stateId, countyId]);

  // bumped by Search/Reset buttons to force a refetch even when page/pageSize don't change
  const [searchNonce, setSearchNonce] = useState(0);

  // Search/state/county filters only take effect via the Search/Reset buttons (searchNonce),
  // but pagination (page, pageSize) must always trigger a refetch on its own
  useEffect(() => {
    fetchReps();
    // Intentionally omit fetchReps to avoid refetch on every callback identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMain, page, pageSize, searchNonce]);

  // Server-side search: server returns current page of reps and meta.total
  const { totalPages, currentPage, start, end, visible } = useMemo(() => {
    const tp = Math.max(1, Math.ceil((meta.total || reps.length) / pageSize));
    const cp = Math.min(page, tp);
    const s = (cp - 1) * pageSize;
    const e = s + pageSize;
    return { totalPages: tp, currentPage: cp, start: s, end: e, visible: reps };
  }, [meta.total, reps, page, pageSize]);

  // memoized set of selected ids for faster lookup in row render
  const selectedIdsSet = useMemo(() => new Set((selected || []).map((s) => s.id)), [selected]);

  // memoized pages array for paginator
  const pages = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages]);

  // stable handler to toggle active state for a rep
  const handleToggleActive = useCallback(async (rep) => {
    const newState = !rep.is_active;
    try {
      await api.put(`/admin/sales-reps/${selectedMain}/${rep.id}`, { is_active: newState });
      showToast(newState ? 'Enabled' : 'Disabled', 'success');
      await fetchReps();
    } catch (err) {
      console.error('Toggle failed', err);
      showToast('Could not update status', 'error');
    }
  }, [selectedMain, fetchReps, showToast]);

  // selection helpers (currently unused)

  // create / edit handlers
  const resetSecurityFields = useCallback(() => {
    setChangePassword(false);
    setEditPassword("");
    setEditConfirmPassword("");
    setChangeUsername(false);
    setEditUsername("");
    setCreateUsername("");
    setCreatePassword("");
    setCreateConfirmPassword("");
    usernameValidRef.current = null;
  }, []);

  const validateSecurityForSave = () => {
    if (changePassword) {
      if (editPassword.length < 6 || editPassword.length > 15) {
        setFormError("Password must be 6–15 characters.");
        return false;
      }
      if (editPassword !== editConfirmPassword) {
        setFormError("Password and Confirm Password do not match.");
        return false;
      }
    }
    if (changeUsername && !editUsername.trim()) {
      setFormError("Username is required when changing username.");
      return false;
    }
    return true;
  };

  const openCreate = () => {
    setForm(initialFormSplit);
    setFormError("");
    setErrors({});
    resetSecurityFields();
    emailValidRef.current = null;
    // reset modal-local selections (multi-select)
    setModalStateIds([]);
    setModalCountiesMap({});
    // also clear the shared county cache so selecting the same state after reopening
    // will trigger a fresh API fetch (previous behavior relied on modal-local cache)
    if (typeof setCache === 'function') setCache({});
    setModalSelectedCounties({});
    setModalMissing({});
    setModalMissingUsers([]);
    setModal({ type: "create" });
  };

  // Handlers for modal controls (stable callbacks to avoid inline functions)
  const handleCreateStatesChange = useCallback(async (e) => {
    const opts = Array.from(e.target.selectedOptions).map(o => parseOptionValue(o.value));
    setModalStateIds(opts);
    await Promise.all(opts.map((sid) => fetchCountiesForState(sid)));
    setModalSelectedCounties((m) => {
      const copy = { ...m };
      opts.forEach((sid) => { if (!copy[sid]) copy[sid] = copy[sid] || []; });
      Object.keys(copy).forEach((k) => { if (!opts.includes(parseOptionValue(k))) delete copy[k]; });
      return copy;
    });
  }, [fetchCountiesForState]);

  // Address field handlers (for the Sales Rep Address section)
  const handleAddressFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const handleZipChange = useCallback((e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 5);
    setForm((f) => ({ ...f, address_zip: digitsOnly }));
  }, []);

  const handleAddressStateChange = useCallback(async (e) => {
    const val = parseOptionValue(e.target.value);
    // clear address_county when state changes
    setForm((f) => ({ ...f, address_state: val, address_county: "" }));
    if (val) await fetchCountiesForState(val);
  }, [fetchCountiesForState]);

  const handleAddressCountyChange = useCallback((e) => {
    const val = parseOptionValue(e.target.value);
    setForm((f) => ({ ...f, address_county: val }));
  }, []);

  const handleToggleStateCheckbox = useCallback(async (sid, checked) => {
    if (checked) {
      setModalStateIds((arr) => arr.includes(sid) ? arr : [...arr, sid]);
      await fetchCountiesForState(sid);
      setModalSelectedCounties((m) => ({ ...m, [sid]: m[sid] || [] }));
    } else {
      setModalStateIds((arr) => arr.filter((x) => x !== sid));
      setModalSelectedCounties((m) => {
        const copy = { ...m };
        delete copy[sid];
        return copy;
      });
    }
  }, [fetchCountiesForState]);

  const handleToggleCounty = useCallback((sid, cid, checked) => {
    setModalSelectedCounties((m) => {
      const cur = m[sid] ? [...m[sid]] : [];
      if (checked) {
        if (!cur.includes(cid)) cur.push(cid);
      } else {
        const idx = cur.indexOf(cid);
        if (idx >= 0) cur.splice(idx, 1);
      }
      return { ...m, [sid]: cur };
    });
  }, []);

  const handleRemoveCountyTag = useCallback((sid, cid) => {
    setModalSelectedCounties((m) => {
      const cur = (m[sid] || []).filter((x) => x !== cid);
      return { ...m, [sid]: cur };
    });
  }, []);

  const handleEmailBlur = useCallback(async (e) => {
    const email = typeof e === "string" ? e : e?.target?.value;
    if (!email || !selectedMain) return;
    try {
      const res = await api.get("/auth/check-email", {
        params: { email, maincompanyid: selectedMain, user_type: form.user_type || "S" },
      });
      if (res.data?.status !== "success") {
        emailValidRef.current = false;
        setErrors((prev) => ({ ...prev, email: "Email already exists." }));
      } else {
        emailValidRef.current = true;
        setErrors((prev) => {
          const next = { ...prev };
          delete next.email;
          return next;
        });
      }
    } catch {
      emailValidRef.current = false;
      setErrors((prev) => ({ ...prev, email: "Email validation failed. Please try again." }));
    }
  }, [selectedMain, form.user_type]);

  const handleEmailChange = useCallback(() => {
    emailValidRef.current = null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  }, []);

  const handleCreateUsernameBlur = useCallback(async (e) => {
    const username = typeof e === "string" ? e : e?.target?.value;
    if (!username || !selectedMain) return;
    try {
      const res = await api.get("/auth/check-username", {
        params: { username, maincompanyid: selectedMain },
      });
      if (res.data?.status !== "success") {
        usernameValidRef.current = false;
        setErrors((prev) => ({ ...prev, username: "Username already exists." }));
      } else {
        usernameValidRef.current = true;
        setErrors((prev) => {
          const next = { ...prev };
          delete next.username;
          return next;
        });
      }
    } catch {
      usernameValidRef.current = false;
      setErrors((prev) => ({ ...prev, username: "Username validation failed. Please try again." }));
    }
  }, [selectedMain]);

  const handleCreateUsernameChange = useCallback((value) => {
    setCreateUsername(value);
    usernameValidRef.current = null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.username;
      return next;
    });
  }, []);

  const handleRepUserTypeChange = useCallback((e) => {
    const value = e.target.value;
    if (value !== "S" && value !== "SM") return;
    setForm((f) => ({ ...f, user_type: value }));
    emailValidRef.current = null;
    setErrors((prev) => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  }, []);

  const openEdit = async (rep) => {
    setFormError("");
    setErrors({});
    setEditImgError(false);
    resetSecurityFields();
    emailValidRef.current = null;
    // show a quick loading state in modal by opening it (optional)
    setModal({ type: "edit", data: null });
    setModalMissing({});
    setModalMissingUsers([]);
    try {
      setSaving(true);
      // Fetch rep details from admin endpoint
      const resp = await api.get(`/admin/sales-reps/${selectedMain}/${rep.id}`);
      if (!resp) throw new Error('Failed to fetch sales rep details');
      const body = resp.data;
      const full = body?.data || body;

      // populate form fields (include new address fields)
      const rawAddressState = full.address_state || full.addressstate || full.address_state_id || full.address_stateid || full.stateid || full.state_id || '';
      const rawAddressCounty = full.address_county || full.addresscounty || full.address_county_id || full.address_countyid || full.countyid || full.county_id || '';
      const rawUt = full.acct_type ?? full.acctType ?? full.user_type ?? full.userType ?? full.usertype;
      const rawUtStr = rawUt != null && rawUt !== "" ? String(rawUt).trim() : "";
      const normalizedUt =
        rawUtStr.toUpperCase() === "SM" || rawUtStr === "Sales Rep Manager" ? "SM" : "S";

      const rawCf = full.do_not_show_on_cf ?? full.doNotShowOnCf;
      const doNotShowOnCf = rawCf === true || rawCf === 1 || rawCf === '1' || String(rawCf).toLowerCase() === 'true';

      setForm({
        user_type: normalizedUt,
        do_not_show_on_cf: doNotShowOnCf,
        firstname: full.firstname || '',
        lastname: full.lastname || '',
        email: full.email || '',
        phone: full.phone || '',
        title: full.title || '',
        address1: full.address1 || '',
        address2: full.address2 || '',
        address_city: full.address_city || full.addresscity || '',
        address_state: parseOptionValue(rawAddressState) || '',
        address_county: parseOptionValue(rawAddressCounty) || '',
        address_zip: full.address_zip || full.addresszip || ''
      });

      // initialize selections from selected_locations if present
      const sel = Array.isArray(full.selected_locations) ? full.selected_locations : [];
      if (sel.length) {
        // collect unique state ids and build selected counties map
        const stateIds = [];
        const selCounties = {};
        for (const s of sel) {
          const sid = s.state_id || s.stateid || s.id;
          if (sid == null) continue;
          if (!stateIds.includes(sid)) stateIds.push(sid);
          const counties = Array.isArray(s.counties) ? s.counties : [];
          selCounties[sid] = counties.map((c) => c.county_id || c.countyid || c.id).filter((x) => x != null);
        }

        // fetch county lists once per unique state in parallel
        await Promise.all(stateIds.map((sid) => fetchCountiesForState(sid)));

        setModalStateIds(stateIds);
        setModalSelectedCounties(selCounties);
      } else {
        // fallback to legacy single state/county
        const sId = full.stateid || full.state_id || null;
        const cId = full.countyid || full.county_id || null;
        if (sId) {
          await fetchCountiesForState(sId);
          setModalStateIds([sId]);
          setModalSelectedCounties({ [sId]: cId ? [cId] : [] });
        } else {
          setModalStateIds([]);
          setModalSelectedCounties({});
        }
      }
      setSaving(false);
      // set modal data with the full object
      setModal({ type: 'edit', data: full });
    } catch (err) {
      console.error('Failed to load sales rep for edit', err);
      showToast('Failed to load sales rep details', 'error');
      setModal({ type: null });
    }
  };

  // Deep link support: opened from the combined Users/Sales Reps list with ?repId=<id>
  // (mirrors AdminUsers.js's existing ?userId= deep link, adapted since this page's
  // edit flow is a single async fetch-then-open rather than a multi-step wizard).
  const deepLinkRepIdRef = useRef(null);
  useEffect(() => {
    if (!selectedMain) return;
    const params = new URLSearchParams(location.search);
    const repId = params.get('repId');
    if (!repId || deepLinkRepIdRef.current === repId) return;
    deepLinkRepIdRef.current = repId;
    openEdit({ id: repId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, selectedMain]);

  const validateForm = () => {
    const e = {};
    const first = (form.firstname || '').trim();
    const last = (form.lastname || '').trim();
    if (!first && !last) e.name = 'First or last name is required.';

    const email = (form.email || '').trim();
    if (!email) e.email = 'Email is required.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) e.email = 'Invalid email.';
    else if (emailValidRef.current === false) e.email = 'Email already exists.';

    // US phone validation (basic)
    // if (form.phone && !/^\+?1?\s*\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(form.phone)) e.phone = 'Invalid US phone number.';

    if (!((form.title || '').trim())) e.title = 'Title is required.';

    if (!modalStateIds || modalStateIds.length === 0) {
      e.state = 'State is required.';
    }

    // ensure at least one county selected across all selected states
    const totalSelectedCounties = Object.values(modalSelectedCounties || {}).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
    if (totalSelectedCounties === 0) e.county = 'County is required.';

    setErrors(e);
    // return null when no errors
    return Object.keys(e).length ? e : null;
  };

  const validateCreateAccount = () => {
    const e = {};
    const username = (createUsername || '').trim();
    if (!username) e.username = 'Username is required.';
    else if (usernameValidRef.current === false) e.username = 'Username already exists.';

    const pwd = createPassword || '';
    if (!pwd) e.password = 'Password is required.';
    else if (pwd.length < 6) e.password = 'Password must be at least 6 characters.';
    else if (!/[a-z]/.test(pwd)) e.password = 'Password must include a lowercase letter.';
    else if (!/\d/.test(pwd)) e.password = 'Password must include a number.';
    else if (!/[=!\-@._&%$#?'"<>^*]/.test(pwd)) e.password = "Password must include a special character.";

    const confirm = createConfirmPassword || '';
    if (!confirm) e.confirmPassword = 'Please confirm the password.';
    else if (confirm !== pwd) e.confirmPassword = 'Passwords do not match.';

    setErrors((prev) => ({ ...prev, ...e }));
    return Object.keys(e).length ? e : null;
  };

  const handleSaveCreate = async () => {
    const v = validateForm();
    const va = validateCreateAccount();
    if (v || va) {
      setFormError('Please fix errors below.');
      return;
    }
    setFormError("");
    try {
      setSaving(true);
      // build payload including selected_locations structure
      const { selected_locations, primaryStateId, primaryCountyId } = buildSelectedLocations({ modalStateIds, modalSelectedCounties, modalCountiesMap, states });

      const payload = {
        firstname: form.firstname || '',
        lastname: form.lastname || '',
        email: form.email || '',
        phone: form.phone || '',
        title: form.title || '',
        maincompanyid: selectedMain,
        stateid: primaryStateId || null,
        countyid: primaryCountyId || null,
        address1: form.address1 || '',
        address2: form.address2 || '',
        address_city: form.address_city || '',
        address_state: form.address_state || null,
        address_county: form.address_county || null,
        address_zip: form.address_zip || '',
        selected_locations,
        user_type: form.user_type === "SM" ? "SM" : "S",
        username: createUsername.trim(),
        password: createPassword,
      };

      await api.post(`/admin/sales-reps/${selectedMain}`, payload);
      setModal({ type: null });
      await fetchReps();
      setSelected([]);
      showToast('Sales rep created', 'success');
    } catch (err) {
      console.error("Create failed", err);
      const msg = err?.response?.data?.message || "Failed to create.";
      setFormError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    const v = validateForm();
    if (v) {
      setFormError('Please fix errors below.');
      return;
    }
    setFormError("");
    if (!validateSecurityForSave()) return;
    try {
      // build selected_locations and primary state/county
      const { selected_locations, primaryStateId, primaryCountyId } = buildSelectedLocations({ modalStateIds, modalSelectedCounties, modalCountiesMap, states });

      const payload = {
        firstname: form.firstname || '',
        lastname: form.lastname || '',
        email: form.email || '',
        phone: form.phone || '',
        title: form.title || '',
        maincompanyid: selectedMain,
        stateid: primaryStateId || null,
        countyid: primaryCountyId || null,
        address1: form.address1 || '',
        address2: form.address2 || '',
        address_city: form.address_city || '',
        address_state: form.address_state || null,
        address_county: form.address_county || null,
        address_zip: form.address_zip || '',
        selected_locations,
        user_type: form.user_type === "SM" ? "SM" : "S",
        do_not_show_on_cf: !!form.do_not_show_on_cf,
      };
      if (changePassword) payload.password = editPassword;
      if (changeUsername) payload.username = editUsername.trim();
      await api.put(`/admin/sales-reps/${selectedMain}/${modal.data.id}`, payload);
      setModal({ type: null });
      await fetchReps();
      setSelected([]);
      showToast('Sales rep updated', 'success');
    } catch (err) {
      console.error("Edit failed", err);
      const status = err?.response?.status;
      const body = err?.response?.data;
      // handle special 400 case where server returns missing_state_county and users
      if (status === 400 && body && Array.isArray(body.missing_state_county) && body.missing_state_county.length) {
        await handleMissingStateCounty(body);
        return;
      }
      setFormError(err.response?.data?.message || "Failed to save.");
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/sales-reps/${selectedMain}/${id}`);
      setModal({ type: null });
      await fetchReps();
      setSelected([]);
      showToast('Sales rep deleted', 'success');
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 400 && msg) {
        // show an informational modal with the server-provided message
        setModal({ type: 'info', data: { message: 'Cannot remove this sales representative because it is assigned to active users. Please unassign the sales representative from those users first' } });
        return;
      }
      const errMsg = msg || 'Delete failed. Try again.';
      setError(errMsg);
      showToast(errMsg, 'error');
    }
  };

  const handleUploadImage = async (rep, file) => {
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('profile', file); // The backend expects 'profile' as the field name

      await api.post(`/admin/sales-reps/${selectedMain}/${rep.id}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setModal({ type: null });
      await fetchReps();
      showToast('Profile image uploaded successfully', 'success');
    } catch (err) {
      console.error('Upload failed', err);
      const msg = err?.response?.data?.message || 'Failed to upload image. Please try again.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // centralize handling of 400 missing_state_county responses
  const handleMissingStateCounty = useCallback(async (body) => {
    try {
      const missingMap = {};
      (Array.isArray(body.missing_state_county) ? body.missing_state_county : []).forEach((s) => {
        const rawSid = s.state_id || s.stateid || s.id;
        const sid = parseOptionValue(rawSid);
        const counties = Array.isArray(s.counties) ? s.counties : [];
        missingMap[sid] = counties.map((c) => c.county_id || c.countyid || c.id).filter((x) => x != null);
      });
      setModalMissing(missingMap);
      setModalMissingUsers(Array.isArray(body.users) ? body.users : []);
      // ensure county lists cached for highlighted states so UI can show county names
      const keys = Object.keys(missingMap);
      if (keys.length) {
        await Promise.allSettled(keys.map((k) => fetchCountiesForState(parseOptionValue(k))));
      }
      const userNames = (Array.isArray(body.users) ? body.users.map((u) => u.userFullName || u.userFullname || u.name).filter(Boolean) : []).join(', ');
      setFormError(`${body.message || 'Missing state/counties'}${userNames ? ` — Affected users: ${userNames}` : ''}`);
      showToast(body.message || 'Missing state/counties', 'error');
    } catch (e) {
      console.error('Failed handling missing_state_county', e);
      setFormError(body?.message || 'Missing state/counties');
      showToast(body?.message || 'Missing state/counties', 'error');
    }
  }, [fetchCountiesForState, showToast]);

  // handle toggle active (not currently used)

  // UI - loading skeleton
  if (loading) {
    return (
      <div className="p-4">
        <AdminPageHeader
          title="Manage Sales Representatives"
          actions={<div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg"></div>}
          filters={<div className="h-12 w-full bg-gray-200 animate-pulse rounded-lg"></div>}
        />

        {/* Loading Table Skeleton */}
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden mt-6">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 w-12">
                    <div className="h-4 w-32 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[180px] hidden md:table-cell">
                    <div className="h-4 w-28 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[120px] hidden sm:table-cell">
                    <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[120px] hidden lg:table-cell">
                    <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[120px]">
                    <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[100px] hidden xl:table-cell">
                    <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                  <th className="px-4 py-4 min-w-[150px]">
                    <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <div className="h-3 w-3 bg-gray-200 animate-pulse rounded-full"></div>
                          <div className="h-4 w-16 bg-gray-200 animate-pulse rounded-full"></div>
                        </div>
                        <div className="h-4 w-32 bg-gray-200 animate-pulse rounded"></div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="h-4 w-28 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-4 w-24 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4 hidden xl:table-cell">
                      <div className="h-4 w-20 bg-gray-200 animate-pulse rounded"></div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1">
                        <div className="h-6 w-16 bg-gray-200 animate-pulse rounded"></div>
                        <div className="h-6 w-12 bg-gray-200 animate-pulse rounded"></div>
                        <div className="h-6 w-14 bg-gray-200 animate-pulse rounded"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <AdminPageHeader
        title="Manage Sales Representatives"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={openCreate}
              className={`inline-flex items-center gap-1.5 px-3 py-2 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2`}
            >
              <PlusCircle className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Create Sales Rep</span>
              <span className="sm:hidden">Create</span>
            </button>
          </div>
        )}
        filters={(
          <div className="flex flex-wrap items-stretch gap-4 w-full">
            <div className="flex-1 min-w-[300px]">
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-2 flex-1 min-w-[180px]">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Search name, email or phone
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search name, email or phone..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-gray-50 focus:bg-white"
                      />
                      <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      State
                    </label>
                    <select
                      value={stateId}
                      onChange={(e) => { setStateId(Number(e.target.value) || 0); setCountyId(0); }}
                      className="min-w-[140px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                    >
                      <option value={0}>All States</option>
                      {states.map((s) => (
                        <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>{s.statename || s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      County
                    </label>
                    <select
                      value={countyId}
                      onChange={(e) => { setCountyId(Number(e.target.value) || 0); }}
                      className="min-w-[140px] px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white"
                    >
                      <option value={0}>All Counties</option>
                      {counties.map((c) => (
                        <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>{c.countyname || c.name}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={() => {
                      setPage(1);
                      setSearchNonce((n) => n + 1);
                    }}
                    className={`flex-shrink-0 inline-flex items-center gap-2 px-6 py-2.5 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="hidden sm:inline">Search</span>
                  </button>
                  <button
                    onClick={() => {
                      setFilters({ query: "", state: "", county: "", createdFrom: "", createdTo: "" });
                      setSearchQuery("");
                      setStateId(0);
                      setCountyId(0);
                      setPage(1);
                      setSearchNonce((n) => n + 1);
                    }}
                    className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
            {/* Right: stats (column layout to free space for filters) */}
            <div className="flex flex-col gap-2 flex-shrink-0">
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Total Sales Reps</p>
                  <p className="text-base font-bold text-gray-900">{meta.total}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Active Sales Reps</p>
                  <p className="text-base font-bold text-green-600">{visible.filter(r => r.is_active).length}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm flex items-center gap-2 min-w-[140px]">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate">Filtered Results</p>
                  <p className="text-base font-bold text-purple-600">{visible.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      />

      {error && <div className="text-red-600 my-2">{error}</div>}

      <TooltipProvider>
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left min-w-[170px]">
                    <span className="font-semibold text-gray-900 text-sm">Name & Status</span>
                  </th>
                  <th className="px-4 py-4 text-left min-w-[200px] hidden md:table-cell">
                    <span className="font-semibold text-gray-900 text-sm">Email/Phone</span>
                  </th>

                  <th className="px-4 py-4 text-left min-w-[140px] hidden lg:table-cell">
                    <span className="font-semibold text-gray-900 text-sm">Title</span>
                  </th>
                  <th className="px-4 py-4 text-left min-w-[130px]">
                    <span className="font-semibold text-gray-900 text-sm">Service Areas</span>
                  </th>
                  <th className="px-4 py-4 text-left min-w-[100px] hidden xl:table-cell">
                    <span className="font-semibold text-gray-900 text-sm">Created</span>
                  </th>
                  <th className="px-4 py-4 text-left min-w-[120px]">
                    <span className="font-semibold text-gray-900 text-sm">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Sales Reps Found</h3>
                          <p className="text-gray-600 mb-4">
                            {searchQuery || stateId || countyId || filters.createdFrom || filters.createdTo
                              ? "Try adjusting your search criteria or filters."
                              : "Get started by creating your first sales representative."
                            }
                          </p>
                          {(searchQuery || stateId || countyId || filters.createdFrom || filters.createdTo) && (
                            <button
                              onClick={() => {
                                setFilters({ query: "", state: "", county: "", createdFrom: "", createdTo: "" });
                                setSearchQuery("");
                                setStateId(0);
                                setCountyId(0);
                                setPage(1); // Reset to first page
                                setSearchNonce((n) => n + 1); // Trigger search with cleared filters
                              }}
                              className={`inline-flex items-center gap-2 px-4 py-2 ${primaryBtnClasses} rounded-lg transition-colors font-medium`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Clear Filters
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visible.map((r, idx) => {
                    const isSelected = selectedIdsSet.has(r.id);
                    const hideOnCf = !!r.do_not_show_on_cf;
                    const isSm = !!r.is_sales_rep_manager;
                    return (
                      <tr
                        key={r.id}
                        className={`
                          transition-all duration-200 ease-in-out
                          ${isSelected
                            ? "bg-[var(--role-border-light)]/20 border-l-4 border-[var(--role-border-primary)] shadow-sm"
                            : hideOnCf && isSm
                              ? "bg-gradient-to-r from-violet-50/95 to-amber-50/95 hover:from-violet-50 hover:to-amber-50 border-l-4 border-violet-600"
                              : hideOnCf
                                ? "bg-amber-50/80 hover:bg-amber-50 border-l-4 border-amber-400"
                                : isSm
                                  ? "bg-violet-50/85 hover:bg-violet-50 border-l-4 border-violet-500"
                                  : idx % 2 === 0
                                    ? "bg-white hover:bg-gray-50"
                                    : "bg-gray-25 hover:bg-gray-50"
                          }
                          border-b border-gray-100
                        `}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            {/* Profile Image */}
                            <div className="flex-shrink-0">
                              {r.rep_image ? (
                                <img
                                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}${r.rep_image}`}
                                  alt={`${r.name} profile`}
                                  className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={() => setModal({ type: "uploadImage", data: r })}
                                      className="w-8 h-8 rounded-full bg-amber-50 border-2 border-dashed border-amber-400 flex items-center justify-center hover:bg-amber-100 hover:border-amber-500 transition-colors cursor-pointer group"
                                      aria-label="Add profile picture"
                                    >
                                      <Camera className="w-4 h-4 text-amber-600 group-hover:text-amber-700" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="right">
                                    Add profile picture
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>

                            {/* Status Indicators */}
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-3 h-3 rounded-full shadow-sm ${r.is_active ? "bg-green-500" : "bg-red-500"}`}
                                  title={r.is_active ? "Active Sales Rep" : "Inactive Sales Rep"}
                                ></div>
                              </div>
                            </div>

                            {/* Sales Rep Name */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-gray-900 text-sm">{r.name}</span>
                                {isSm && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="inline-flex items-center shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-900 ring-1 ring-inset ring-violet-500/30 cursor-default"
                                      >
                                        SM
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      Sales Rep Manager
                                      </TooltipContent>
                                  </Tooltip>
                                )}
                                {hideOnCf && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="inline-flex items-center shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-500/25 cursor-default"
                                      >
                                        CF off
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      Do not show on CF — this rep is hidden from Frontend.
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-xs text-gray-700 font-mono" title={r.email}>
                            {r.email}<br />
                            <a href={`tel:${r.phone}`} className="text-blue-500">{r.phone || "—"}</a>
                          </span>
                        </td>



                        <td className="px-3 py-3 hidden lg:table-cell">
                          <span className="text-sm text-gray-900">
                            {r.title || "Sales Rep"}
                          </span>
                        </td>

                        <td className="px-3 py-3 max-w-[140px]">
                          <SelectedLocationsDisplay selectedLocations={r.selectedLocations} fallbackState={r.state} compact />
                        </td>

                        <td className="px-4 py-4 hidden xl:table-cell">
                          <span className="text-sm text-gray-600">
                            {r.created}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1 flex-nowrap">
                            <button
                              onClick={() => handleToggleActive(r)}
                              title={r.is_active ? 'Disable' : 'Enable'}
                              className={`p-1.5 rounded-md transition-colors flex items-center justify-center shrink-0 ${r.is_active
                                ? 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'
                                : 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200'
                                }`}
                            >
                              {r.is_active ? <ShieldOff className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => setModal({ type: "uploadImage", data: r })}
                              title="Upload image"
                              className="p-1.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center justify-center shrink-0"
                            >
                              <Camera className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openEdit(r)}
                              title="Edit"
                              className={`p-1.5 rounded-md ${primaryBtnClasses} transition-colors flex items-center justify-center shrink-0`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setModal({ type: "confirmDelete", data: r })}
                              title="Delete"
                              className="p-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center shrink-0"
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </TooltipProvider>

      {/* Enhanced Pagination */}
      {meta.total > 0 && (
        <div className="flex flex-wrap justify-between items-center gap-4 mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <span className="text-sm font-medium text-gray-700">
            Showing {meta.total ? start + 1 : 0}–{Math.min(end, meta.total)} of {meta.total} sales reps
          </span>

          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Rows per page:</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
            >
              {[10, 25, 50].map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>

          <nav className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
            >
              Previous
            </button>
            {pages.map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] ${currentPage === n
                  ? `${primaryBtnClasses} border-[var(--role-border-primary)]`
                  : "bg-white border-gray-300 hover:bg-gray-50"
                  }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* modals */}
      {modal.type === "create" && (
        <Modal
          title="Create Sales Rep"
          size="4xl"
          onClose={() => setModal({ type: null })}
          style={{ maxHeight: '90vh', height: 'auto' }}
        >
          <div className="space-y-6">

            {/* Company and User Type Information */}
            <div className="col-span-2 mb-6">
              <div
                className="border rounded-xl p-4 shadow-sm"
                style={{
                  background: roleTheme?.gradientBg?.primary || 'linear-gradient(135deg, rgba(239, 246, 255, 0.95), rgba(238, 242, 255, 0.9))',
                  borderColor: roleTheme?.colors?.sidebar?.hover || '#bfdbfe'
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Company Information */}
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: '#dbeafe',
                        color: '#2563eb'
                      }}
                    >
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs font-medium uppercase tracking-wide"
                        style={{ color: roleTheme?.colors?.sidebar?.active || '#1d4ed8' }}
                      >
                        Company
                      </p>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {mainCompanies.find(company => company.id === selectedMain)?.main_company_name || 'Unknown Company'}
                      </p>
                    </div>
                  </div>

                  {/* User type: drives /auth/check-email and POST/PUT user_type */}
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: '#dcfce7',
                        color: '#16a34a'
                      }}
                    >
                      <User className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="sales-rep-create-user-type"
                        className="block text-xs font-medium uppercase tracking-wide mb-1"
                        style={{ color: roleTheme?.colors?.accents?.success || '#15803d' }}
                      >
                        User type
                      </label>
                      <select
                        id="sales-rep-create-user-type"
                        value={form.user_type === "SM" ? "SM" : "S"}
                        onChange={handleRepUserTypeChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      >
                        <option value="S">Sales Rep</option>
                        <option value="SM">Sales Rep Manager</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-medium">{formError}</p>
                </div>
              </div>
            )}

            {modalMissingUsers && modalMissingUsers.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-800 font-medium mb-1">Affected users:</p>
                    <ul className="list-disc ml-5 text-yellow-700">
                      {modalMissingUsers.map((u, i) => (
                        <li key={i}>{u.userFullName || u.userFullname || u.name || JSON.stringify(u)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Personal Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Personal Information</h3>
              </div>
              <PersonalFields form={form} setForm={setForm} errors={errors} onEmailBlur={handleEmailBlur} onEmailChange={handleEmailChange} />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={createUsername}
                    onChange={(e) => handleCreateUsernameChange(e.target.value)}
                    onBlur={handleCreateUsernameBlur}
                    className="w-full border rounded p-1.5 text-sm"
                    autoComplete="username"
                  />
                  {errors.username && (
                    <div className="text-red-600 text-xs mt-1">{errors.username}</div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={createPassword}
                    onChange={(e) => setCreatePassword(e.target.value)}
                    className="w-full border rounded p-1.5 text-sm"
                    autoComplete="new-password"
                  />
                  {errors.password ? (
                    <div className="text-red-600 text-xs mt-1">{errors.password}</div>
                  ) : (
                    <p className="text-gray-500 text-xs mt-1">
                      At least 6 characters, with a lowercase letter, a number, and a special character (e.g. ! @ # $ %).
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={createConfirmPassword}
                    onChange={(e) => setCreateConfirmPassword(e.target.value)}
                    className="w-full border rounded p-1.5 text-sm"
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <div className="text-red-600 text-xs mt-1">{errors.confirmPassword}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Service Areas Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Service Areas</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    States <span className="text-red-500">*</span>
                  </label>
                  <StatesSelectorMulti states={states} modalStateIds={modalStateIds} onChange={handleCreateStatesChange} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Counties (select for each state) <span className="text-red-500">*</span>
                  </label>
                  <CountiesPanel
                    modalStateIds={modalStateIds}
                    states={states}
                    modalCountiesMap={modalCountiesMap}
                    modalSelectedCounties={modalSelectedCounties}
                    modalMissing={modalMissing}
                    onToggleCounty={handleToggleCounty}
                    onRemoveCountyTag={handleRemoveCountyTag}
                  />
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Home className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Address Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address1"
                    value={form.address1}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter street address"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Address 2
                  </label>
                  <input
                    name="address2"
                    value={form.address2}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter address line 2"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address_city"
                    value={form.address_city}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter city"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="address_state"
                    value={form.address_state || ''}
                    onChange={handleAddressStateChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>
                        {s.statename || s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    County <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="address_county"
                    value={form.address_county || ''}
                    onChange={handleAddressCountyChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select County</option>
                    {(modalCountiesMap[form.address_state] || []).map((c) => (
                      <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>
                        {c.countyname || c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address_zip"
                    value={form.address_zip}
                    onChange={handleZipChange}
                    maxLength={5}
                    inputMode="numeric"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter ZIP code"
                  />
                </div>
              </div>
            </div>       

          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              onClick={() => setModal({ type: null })}
            >
              Cancel
            </button>
            {saving ? (
              <button
                disabled
                className={`px-4 py-2 ${primaryBtnClasses} rounded-lg font-medium flex items-center gap-2 opacity-75 cursor-not-allowed`}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </button>
            ) : (
              <button
                className={`px-4 py-2 ${primaryBtnClasses} rounded-lg hover:opacity-90 transition-colors font-medium`}
                onClick={handleSaveCreate}
              >
                Create Sales Rep
              </button>
            )}
          </div>

        </Modal>
      )}

      {modal.type === "edit" && (
        <Modal
          title={"Edit Sales Rep"}
          size="4xl"
          onClose={() => {
            setEditProfilePreviewOpen(false);
            setModal({ type: null });
          }}
          maxWidth="max-w-3xl"
          style={{ maxHeight: '90vh', height: 'auto' }}
        >
          <div className="space-y-6">
            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 font-medium">{formError}</p>
                </div>
              </div>
            )}

            {/* Company and User Type Information */}
            <div className="col-span-2 mb-0">
              <div
                className="border rounded-xl p-4 shadow-sm"
                style={{
                  background: roleTheme?.gradientBg?.primary || 'linear-gradient(135deg, rgba(239, 246, 255, 0.95), rgba(238, 242, 255, 0.9))',
                  borderColor: roleTheme?.colors?.sidebar?.hover || '#bfdbfe'
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: '#dbeafe',
                        color: '#2563eb'
                      }}
                    >
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs font-medium uppercase tracking-wide"
                        style={{ color: roleTheme?.colors?.sidebar?.active || '#1d4ed8' }}
                      >
                        Company
                      </p>
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {mainCompanies.find(company => company.id === selectedMain)?.main_company_name || 'Unknown Company'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: '#dcfce7',
                        color: '#16a34a'
                      }}
                    >
                      <User className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs font-medium uppercase tracking-wide mb-1"
                        style={{ color: roleTheme?.colors?.accents?.success || '#15803d' }}
                      >
                        User type
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {form.user_type === "SM" ? "Sales Rep Manager" : "Sales Rep"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Picture & Rep Name Header */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex-shrink-0 relative">
                {modal.data?.rep_image && !editImgError ? (
                  <button
                    type="button"
                    onClick={() => setEditProfilePreviewOpen(true)}
                    className="group relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                    aria-label="View profile picture preview"
                  >
                    <img
                      src={`${config.assetsBaseUrl}${modal.data.rep_image}`}
                      alt={`${form.firstname || ''} ${form.lastname || ''}`.trim() || 'Profile'}
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm transition group-hover:brightness-95"
                      onError={() => setEditImgError(true)}
                    />
                    <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center pointer-events-none">
                      <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" aria-hidden />
                    </span>
                  </button>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-amber-50 border-2 border-dashed border-amber-400 flex items-center justify-center">
                    <Camera className="w-10 h-10 text-amber-500" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {`${form.firstname || ''} ${form.lastname || ''}`.trim() || 'Sales Rep'}
                </h3>
                {form.title && (
                  <p className="text-sm text-gray-600 mt-0.5">{form.title}</p>
                )}
                <button
                  type="button"
                  onClick={() => setModal({ type: "uploadImage", data: modal.data })}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Camera className="w-3.5 h-3.5" />
                  {modal.data?.rep_image ? 'Change photo' : 'Add profile picture'}
                </button>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Personal Information</h3>
              </div>
              <PersonalFields form={form} setForm={setForm} errors={errors} onEmailBlur={handleEmailBlur} onEmailChange={handleEmailChange} />
            </div>

            {/* Service Areas Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Service Areas</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    States (select one or more) <span className="text-red-500">*</span>
                  </label>
                  <StatesCheckboxList states={states} modalStateIds={modalStateIds} modalMissing={modalMissing} onToggle={handleToggleStateCheckbox} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Counties (select for each state) <span className="text-red-500">*</span>
                  </label>
                  <div className="grid md:grid-cols-1 gap-4">
                    <CountiesPanel
                      modalStateIds={modalStateIds}
                      states={states}
                      modalCountiesMap={modalCountiesMap}
                      modalSelectedCounties={modalSelectedCounties}
                      modalMissing={modalMissing}
                      onToggleCounty={handleToggleCounty}
                      onRemoveCountyTag={handleRemoveCountyTag}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Home className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Address Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address1"
                    value={form.address1}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter street address"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Address 2
                  </label>
                  <input
                    name="address2"
                    value={form.address2}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter address line 2"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address_city"
                    value={form.address_city}
                    onChange={handleAddressFieldChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter city"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="address_state"
                    value={form.address_state || ''}
                    onChange={handleAddressStateChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>
                        {s.statename || s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    County <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="address_county"
                    value={form.address_county || ''}
                    onChange={handleAddressCountyChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select County</option>
                    {(modalCountiesMap[form.address_state] || []).map((c) => (
                      <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>
                        {c.countyname || c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="address_zip"
                    value={form.address_zip}
                    onChange={handleZipChange}
                    maxLength={5}
                    inputMode="numeric"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter ZIP code"
                  />
                </div>
              </div>
            </div>

            {/* Security Settings (same pattern as AdminUsers edit) */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-4 h-4 text-orange-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-800">Security Settings</h3>
              </div>

              <div className="space-y-4">
               <div className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="repEditChangeUsername"
                      checked={changeUsername}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setChangeUsername(checked);
                        if (checked) setEditUsername(repUsernameFromApi(modal.data) || "");
                      }}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="repEditChangeUsername" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Change Username
                    </label>
                  </div>

                  {changeUsername && (
                    <div className="ml-6 space-y-1">
                      <label className="block text-xs font-medium text-gray-600">
                        New Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editUsername}
                        onChange={(e) => setEditUsername(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Enter new username"
                      />
                    </div>
                  )}
                </div>

                <div className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="repEditChangePassword"
                      checked={changePassword}
                      onChange={(e) => setChangePassword(e.target.checked)}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="repEditChangePassword" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Change Password
                    </label>
                  </div>

                  {changePassword && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-6">
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-600">
                          New Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                          placeholder="Enter new password"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-600">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={editConfirmPassword}
                          onChange={(e) => setEditConfirmPassword(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="repEditDoNotShowOnCf"
                      checked={!!form.do_not_show_on_cf}
                      onChange={(e) => setForm((f) => ({ ...f, do_not_show_on_cf: e.target.checked }))}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="repEditDoNotShowOnCf" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Do not show on CF
                    </label>
                  </div>
                </div>

                
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                onClick={() => {
                  setEditProfilePreviewOpen(false);
                  setModal({ type: null });
                }}
              >
                Cancel
              </button>
              {saving ? (
                <button
                  disabled
                  className={`px-4 py-2 ${primaryBtnClasses} rounded-lg font-medium flex items-center gap-2 opacity-75 cursor-not-allowed`}
                >
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </button>
              ) : (
                <button
                  className={`px-4 py-2 ${primaryBtnClasses} rounded-lg hover:opacity-90 transition-colors font-medium`}
                  onClick={handleSaveEdit}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {editProfilePreviewOpen && modal.type === 'edit' && modal.data?.rep_image && !editImgError && (
        <Modal
          title="Profile picture"
          size="2xl"
          maxHeight="max-h-[90vh] overflow-auto"
          overlayClassName="z-[60]"
          onClose={() => setEditProfilePreviewOpen(false)}
        >
          <div className="flex flex-col items-center gap-2">
            <img
              src={`${config.assetsBaseUrl}${modal.data.rep_image}`}
              alt={`${form.firstname || ''} ${form.lastname || ''}`.trim() || 'Profile preview'}
              className="max-h-[75vh] w-full max-w-full rounded-lg object-contain shadow-md border border-gray-100 bg-gray-50"
            />
          </div>
        </Modal>
      )}

      {modal.type === "confirmDelete" && (
        <Modal title="Delete Sales Rep" onClose={() => setModal({ type: null })}>
          <p className="text-gray-700 mb-4">Are you sure you want to permanently delete <b>{salesRepDisplayName(modal.data)}</b>? This action cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              onClick={() => setModal({ type: null })}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              onClick={() => handleDelete(modal.data.id)}
            >
              Delete Sales Rep
            </button>
          </div>
        </Modal>
      )}

      {modal.type === 'info' && (
        <Modal title="Info" onClose={() => setModal({ type: null })}>
          <p className="text-gray-700 mb-4">{modal.data?.message || 'Info'}</p>
          <div className="flex justify-end">
            <button className={`px-3 py-1 ${primaryBtnClasses} rounded hover:opacity-90`} onClick={() => setModal({ type: null })}>OK</button>
          </div>
        </Modal>
      )}

      {modal.type === "uploadImage" && (
        <Modal
          title={`Upload Profile Picture for ${salesRepDisplayName(modal.data)}`}
          onClose={() => setModal({ type: null })}
        >
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Select a profile image for {salesRepDisplayName(modal.data)}. Supported formats: JPG, PNG, GIF. Maximum size: 1MB.
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Basic validation
                      if (file.size > 1024 * 1024) { // 1MB
                        showToast('File size must be less than 1MB', 'error');
                        e.target.value = '';
                        return;
                      }
                      if (!file.type.startsWith('image/')) {
                        showToast('Please select an image file', 'error');
                        e.target.value = '';
                        return;
                      }
                      setModal(prev => ({ ...prev, selectedFile: file }));
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>

              {modal.selectedFile && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <img
                      src={URL.createObjectURL(modal.selectedFile)}
                      alt="Preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{modal.selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{(modal.selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                onClick={() => setModal({ type: null })}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 ${primaryBtnClasses} rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                disabled={!modal.selectedFile || saving}
                onClick={() => handleUploadImage(modal.data, modal.selectedFile)}
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </div>
                ) : (
                  'Upload Image'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </div>
  );
}
